import { ChangeDetectionStrategy, Component, DestroyRef, HostBinding, Input, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import { CRMTokenImages, CrmConfig, Sitecore } from '@frontend/sports/common/client-config-data-access';
import { DateProviderService } from '@frontend/sports/common/core/utils/date';
import { interpolateDoubleBracesString } from '@frontend/sports/common/core/utils/string';
import { PickSourceProvider } from 'packages/sports/web/app/src/option-pick/pick-source.provider';
import { RewardTokenImages, RewardTokenType } from 'packages/sports/web/app/src/tokens-base/token-base.models';
import { Subject, debounceTime } from 'rxjs';

import { BetAndGetToken, FreeBetToken, OddsBoostToken, RewardBannerTrackingConstants, RiskFreeToken } from '../../../../reward-tokens.model';
import { RewardTokensBannerService, TokenCategories } from '../../../../services/reward-tokens-banner.service';
import { RewardTokenLimitation } from '../reward-token-limitations.component';

@Component({
    selector: 'bs-reward-token-footer-details',
    templateUrl: './reward-token-footer-details.component.html',
    styleUrls: ['../../cards/styles/reward-token-card-shared.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false,
})
export class RewardTokenFooterDetailsComponent implements OnInit {
    @HostBinding('class') className = 'reward-token-card-category';
    @Input() expireTokenReward: RewardTokenLimitation[];
    @Input() token: BetAndGetToken | FreeBetToken | RiskFreeToken | OddsBoostToken;
    @Input() promoHubUrlTokenType: string;
    @Input() isRewardBannerView = false;
    @Input() tokenTitle: string;
    @Input() tokenCategories: TokenCategories;
    linkTitle: string;
    expiredDate = '';
    expires = '';
    isDisabled: boolean;
    iconImageUrl: string;
    isRewardDrawerEnabled = this.tokensConfig.tokens.isRewardDrawerEnabled;
    expiryIcon = '';
    private notificationTokenId: string | null;
    private TIME_DELAY = 5000;
    private betNowRedirectClicked = new Subject<void>();
    private readonly destroyRef = inject(DestroyRef);

    constructor(
        public sitecore: Sitecore,
        private rewardTokensBannerService: RewardTokensBannerService,
        private pickSourceProvider: PickSourceProvider,
        private crmTokenimage: CRMTokenImages,
        private tokensConfig: CrmConfig,
    ) {
        this.linkTitle = this.sitecore.crmTokens.cards.Details;
    }

    ngOnInit() {
        const now = DateProviderService.getDate();
        const tomorrow = DateProviderService.buildDate(now.getFullYear(), now.getMonth(), now.getDate() + 2);
        const diff = this.token.expiryDate.getTime() - now.getTime();
        const diffHours = diff / (60 * 60 * 1000);
        this.isDisabled = this.isRewardDrawerEnabled ? this.token.expiryDate < tomorrow : diffHours < 24;
        this.iconImageUrl = this.getRewardTokenImages();
        this.expires = this.rewardTokensBannerService.getTokenExpires(this.token.expiryDate);
        this.rewardTokensBannerService.recentlyClickedToken$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((userTokenId: string | null) => {
            this.notificationTokenId = userTokenId;
        });

        const buttonClickedDebounced = this.betNowRedirectClicked.pipe(debounceTime(this.TIME_DELAY));
        buttonClickedDebounced.subscribe(() => {
            if (this.notificationTokenId === this.token.userTokenId) {
                this.rewardTokensBannerService.setRecentlyClicked(null);
            }
        });
        this.expiryIcon = this.getExpiryIcon();
    }

    betNowRedirect(): void {
        if (this.tokensConfig.tokens.isRewardDrawerEnabled) {
            this.betNowRedirectClicked.next();
        }
        this.rewardTokensBannerService.betNowRedirect(this.token);
        this.trackBetNow();
    }

    private trackBetNow(): void {
        let tokenInfoUrl = '';
        switch (this.token.rewardTokenType) {
            case RewardTokenType.FreeBet:
                tokenInfoUrl = this.setTokenInfoUrl(this.sitecore.crmTokens.cards.FreeBetsDetailsUrl);
                break;
            case RewardTokenType.RiskFreeBet:
                tokenInfoUrl = this.setTokenInfoUrl(this.sitecore.crmTokens.cards.RiskFreeBetDetailsUrl);
                break;
            case RewardTokenType.OddsBoost:
                tokenInfoUrl = this.setTokenInfoUrl(this.sitecore.crmTokens.cards.OddBoostDetailsUrl);
                break;
            case RewardTokenType.BetAndGet:
                tokenInfoUrl = this.setTokenInfoUrl(this.sitecore.crmTokens.cards.BetAndGetDetailsUrl);
                break;
        }
        this.rewardTokensBannerService.trackBetRewardBanner(
            RewardBannerTrackingConstants.Click,
            this.pickSourceProvider.getTracking(),
            'bet now',
            this.token.rewardTokenType,
            tokenInfoUrl,
        );
    }

    private getRewardTokenImages(): string {
        const isDarkMode = this.tokensConfig.tokens.enableDarkModeForRewardDrawer && this.isRewardBannerView;
        if (this.isDisabled) {
            return this.crmTokenimage.crmTokenImages.find((x) => x.name === RewardTokenImages.RewardTokenTimer)?.src!;
        }

        return this.crmTokenimage.crmTokenImages.find(
            (x) => x.name === (isDarkMode ? RewardTokenImages.RewardTokenTimerThemeDark : RewardTokenImages.RewardTokenTimerDark),
        )?.src!;
    }

    private setTokenInfoUrl(tokenUrl: string): string {
        const promoBaseUrl = this.sitecore.crmTokens.links.promotiondetails.url;
        const promoBaseUrlSeparator = promoBaseUrl?.endsWith('/') ? '' : '/';

        return interpolateDoubleBracesString(tokenUrl, {
            promoBaseUrl: promoBaseUrl ?? '',
            promoBaseUrlSeparator,
            userTokenId: this.token.userTokenId,
        });
    }

    private getExpiryIcon(): string {
        const tokenExpiryIcon = this.sitecore.crmTokens.cards.TokenExpiryIcon;

        return this.isDisabled ? `expire-text ${tokenExpiryIcon}` : tokenExpiryIcon;
    }
}
